import {Trans} from '@lingui/macro';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {usePrevious} from '../../../../../../srcCommon/helpers/hookHelpers';
import {useLinkNavigation} from '../../../../../components/common/navigation/useLinkNavigation';
import {IntegrationTypeModel} from '../../../../../models/api/integrationTypesModel';
import {buildSettingsAppsAppDetailsUrl} from '../../../../../models/url/settingsUrlHelpers';
import {
  SettingsAppsAppDetailsTabsEnum,
  SettingsAppsAppDetailsUrl,
} from '../../../../../models/url/settingsUrlModel';
import {SettingsAppsConfigureAppDialogContainer} from './settingsAppsConfigureAppDialogContainer';

/*
 * Props.
 */

interface SettingsAppsConfigureAppButtonProps {
  url: SettingsAppsAppDetailsUrl;
  integrationType: IntegrationTypeModel;
  isIntegrationTypeEnabled: boolean;
  isIntegrationDisabledViaBilling: boolean;
}

/*
 * Style.
 */

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  margin-bottom: 24px;
`;

const StyledButtonContentDiv = styled.div`
  width: 188px;
  text-align: center;
`;

const StyledDialogOnlyWrapperDiv = styled.div`
  margin-bottom: -24px;
`;

/*
 * Component.
 */

export const SettingsAppsConfigureAppButton: FC<SettingsAppsConfigureAppButtonProps> = (props) => {
  const {integrationType, url, isIntegrationTypeEnabled, isIntegrationDisabledViaBilling} = props;
  const [shouldShowDialog, setShouldShowDialog] = useState(url.shouldOpenConfiguration);
  const {navigateToLink} = useLinkNavigation();

  const previousShouldOpenConfiguration = usePrevious(url.shouldOpenConfiguration);
  useEffect(() => {
    if (url.shouldOpenConfiguration && !previousShouldOpenConfiguration) {
      setShouldShowDialog(true);
    }
  }, [url.shouldOpenConfiguration, previousShouldOpenConfiguration]);

  // If the integration doesn't have a configuration we will not render anything and ignore the query
  // param to open the dialog. Also if we are disabling the integration because of billing reasons.
  if (!integrationType.hasConfiguration || isIntegrationDisabledViaBilling) {
    return null;
  }

  const maybeRenderAppDialog = () => {
    if (!shouldShowDialog) {
      return null;
    }
    return (
      <SettingsAppsConfigureAppDialogContainer
        integrationType={integrationType}
        onRequestClose={(detailsPage?: SettingsAppsAppDetailsTabsEnum) => {
          setShouldShowDialog(false);
          if (url.shouldOpenConfiguration) {
            navigateToLink(
              buildSettingsAppsAppDetailsUrl(url.appId, url.previousUrl, detailsPage ?? url.detailsPage),
            );
          }
        }}
      />
    );
  };

  // If the integration is not enabled, we would only want to potentially render the dialog.
  if (!isIntegrationTypeEnabled) {
    return <StyledDialogOnlyWrapperDiv>{maybeRenderAppDialog()}</StyledDialogOnlyWrapperDiv>;
  }
  return (
    <StyledButtonWrapperDiv>
      {isIntegrationTypeEnabled && (
        <Button onClick={() => setShouldShowDialog(true)} hasHover={shouldShowDialog}>
          <StyledButtonContentDiv>
            <Trans id="settings-apps-configure-app-button-label">Configure app</Trans>
          </StyledButtonContentDiv>
        </Button>
      )}
      {maybeRenderAppDialog()}
    </StyledButtonWrapperDiv>
  );
};
